import { useRef } from 'react';
import { Award, Target, TrendingUp, Users } from 'lucide-react';
import { motion, useInView } from 'motion/react';
import { ImageWithFallback } from './figma/ImageWithFallback';

export function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const values = [
    {
      icon: Target,
      title: 'Misión',
      description: 'Desarrollar espacios funcionales, eficientes y estratégicamente ubicados que impulsen la operación logística y comercial de nuestros clientes.',
    },
    {
      icon: Award,
      title: 'Visión',
      description: 'Convertirnos en un referente en infraestructura logística dentro de la Zona Libre de Colón.',
    },
  ];

  return (
    <section id="about" className="py-20 md:py-32 bg-white border-t border-slate-200" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="rounded-[1.5rem] overflow-hidden border border-slate-200 shadow-sm bg-slate-50">
              <div className="aspect-[4/3]">
                <ImageWithFallback
                  src="/images/znl-1.webp"
                  alt="Free Zone Logistics Park - Fachada"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-slate-100 border border-slate-200 -z-10" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-primary/10 bg-gradient-to-br from-white to-slate-50 text-slate-700 mb-6 shadow-sm font-medium text-sm">
              Sobre Nosotros
            </div>

            <h2 className="text-4xl md:text-5xl text-slate-950 mb-6 leading-tight tracking-tight font-bold">
              Nuestra Trayectoria
            </h2>

            <p className="text-lg text-slate-600 mb-5 leading-relaxed">
              Free Zone Logistics Park es un desarrollo impulsado por <strong>Construcciones y Proyectos Leona, S.A.</strong>, enfocado en ofrecer espacios logísticos modernos dentro de la Zona Libre de Colón.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 18 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.08 }}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm hover:shadow-md transition-shadow"
                  role="article"
                  aria-label={value.title}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary text-white flex items-center justify-center flex-shrink-0">
                      <value.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-slate-950 font-semibold mb-1">{value.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
